import * as React from "react";
import { MenuItem, Select } from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { ModalComponentProps } from "../../../types/modal.types";
import { deviceErrorMessage, invalidateDeviceQueries } from "../../../lib/apis/device-cache";
import { getDeviceOrderItems, patchDeviceOrder } from "../../../lib/apis/v1/device.service";
import { getQueryClient } from "../../../config/queryClient.config";
import FailSnackbar from "../../ui/snackbar/fail.component";
import SuccessSnackbar from "../../ui/snackbar/success.component";
import ConfirmModal from "../confirm.modal";

interface DeviceOrderModalProps extends ModalComponentProps {
  deviceId: string | number;
}

export default function DeviceOrderModal({ id, onClose, deviceId }: DeviceOrderModalProps) {
  const [targetId, setTargetId] = React.useState("");
  const [isFront, setIsFront] = React.useState(true);
  const orderQuery = useQuery({
    queryKey: ["device-order-list", deviceId],
    queryFn: () => getDeviceOrderItems(deviceId),
  });

  const mutation = useMutation({
    mutationFn: () => patchDeviceOrder({ id: deviceId, targetId, isFront }),
    onSuccess: async () => {
      await invalidateDeviceQueries(getQueryClient());
      SuccessSnackbar("노출 순서 변경 완료");
      onClose?.();
    },
    onError: (error) => FailSnackbar(deviceErrorMessage(error)),
  });

  return (
    <ConfirmModal
      id={id}
      onClose={onClose}
      title="노출 순서 변경"
      className="w-[560px]"
      rightButton={{
        title: "변경 하기",
        disabled: mutation.isPending || !targetId,
        onClick: () => mutation.mutate(),
      }}
    >
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Select
          className="min-w-[260px]"
          value={targetId}
          displayEmpty
          disabled={orderQuery.isPending || mutation.isPending}
          MenuProps={{
            style: { zIndex: 3000 },
          }}
          onChange={(event) => setTargetId(event.target.value)}
        >
          <MenuItem value="" disabled>기준 기기 선택</MenuItem>
          {(orderQuery.data?.result ?? []).map((item) => (
            <MenuItem key={item.deviceId} value={String(item.deviceId)}>
              {item.deviceName}
            </MenuItem>
          ))}
        </Select>
        <span className="text-[14px] text-black/60">의</span>
        <Select
          className="min-w-[120px]"
          value={isFront ? "front" : "back"}
          disabled={mutation.isPending}
          MenuProps={{
            style: { zIndex: 3000 },
          }}
          onChange={(event) => setIsFront(event.target.value === "front")}
        >
          <MenuItem value="front">앞으로</MenuItem>
          <MenuItem value="back">뒤로</MenuItem>
        </Select>
        {orderQuery.isError && (
          <p role="alert" className="w-full text-red-500">{deviceErrorMessage(orderQuery.error)}</p>
        )}
      </div>
    </ConfirmModal>
  );
}
